import type { BaseReactProps, ReactNodeDef } from '@shared/node-v1.0.0'
import { getPortDef } from '@shared/port-v1.0.0'
import Comp from '../component'

export type Props = BaseReactProps & {
  data: { x: string } & Record<string, number>[]
  dataKey: string
  series?: { name: string, color: string }[]
  orientation?: 'horizontal' | 'vertical'
  withXAxis: boolean
  withYAxis: boolean
  tickLine: 'x' | 'y' | 'xy' | 'none'
  gridAxis: 'x' | 'y' | 'xy' | 'none'
  withTooltip: boolean
  withLegend: boolean
  legendVerticalAlign?: 'top' | 'bottom'
  type: 'default' | 'stacked' | 'percent' | 'waterfall'
  h?: number | string
  w?: number | string
  xAxisLabel?: string
  yAxisLabel?: string
}

export default {
  hashTag: '#pre-release',
  module: { static: Comp },
  inNode: {
    inputs: [
      getPortDef({
        name: 'data',
        displayName: 'Data',
        group: 'Params',
        type: 'array'
      }),
      getPortDef({
        name: 'dataKey',
        displayName: 'Data key',
        group: 'Params',
        type: 'string',
        default: 'x',
      }),
      getPortDef({
        name: 'series',
        displayName: 'Series',
        group: 'Params',
        type: 'array'
      }),
      getPortDef({
        name: 'orientation',
        displayName: 'Orientation',
        group: 'Params',
        type: [
          { label: 'Horizontal', value: 'horizontal' },
          { label: 'Vertical', value: 'vertical' },
        ],
        default: 'horizontal'
      }),
      getPortDef({
        name: 'withXAxis',
        displayName: 'With x axis',
        group: 'Params',
        type: 'boolean',
        default: true,
      }),
      getPortDef({
        name: 'withYAxis',
        displayName: 'With y axis',
        group: 'Params',
        type: 'boolean',
        default: true,
      }),
      getPortDef({
        name: 'tickLine',
        displayName: 'Tick line',
        group: 'Params',
        type: [
          { label: 'x', value: 'x' },
          { label: 'y', value: 'y' },
          { label: 'xy', value: 'xy' },
          { label: 'none', value: 'none' }
        ],
        default: 'x',
      }),
      getPortDef({
        name: 'gridAxis',
        displayName: 'Grid axis',
        group: 'Params',
        type: [
          { label: 'x', value: 'x' },
          { label: 'y', value: 'y' },
          { label: 'xy', value: 'xy' },
          { label: 'none', value: 'none' }
        ],
        default: 'x',
      }),


      getPortDef({
        name: 'withTooltip',
        displayName: 'With tooltip',
        group: 'Styles',
        type: 'boolean',
        default: true,
      }),
      getPortDef({
        name: 'withLegend',
        displayName: 'With legend',
        group: 'Styles',
        type: 'boolean',
        default: true,
      }),
      getPortDef({
        name: 'legendVerticalAlign',
        displayName: 'Legend vertical align',
        group: 'Styles',
        type: [
          { label: 'Top', value: 'top' },
          { label: 'Bottom', value: 'bottom' },
        ],
        default: 'bottom',
        dependsOn: (p: Props) => p.withLegend
      }),
      getPortDef({
        name: 'type',
        displayName: 'Type',
        group: 'Styles',
        type: [
          { label: 'Default', value: 'default' },
          { label: 'Stacked', value: 'stacked' },
          { label: 'Percent', value: 'percent' },
          { label: 'Waterfall', value: 'waterfall' },
        ],
        default: 'default'
      }),

      getPortDef({
        name: 'xAxisLabel',
        displayName: 'X axis label',
        group: 'Styles',
        type: 'string',
      }),
      getPortDef({
        name: 'yAxisLabel',
        displayName: 'Y axis label',
        group: 'Styles',
        type: 'string',
      }),

      getPortDef({
        name: 'w',
        displayName: 'Width',
        group: 'Layout',
        type: 'string'
      }),
      getPortDef({
        name: 'h',
        displayName: 'Height',
        group: 'Layout',
        type: 'string'
      }),
    ],
  },
} satisfies ReactNodeDef
